import type { GroupMetaUpdate, GroupMetaUpdatePlan } from "./projectGroupUpdate";

/** Sends one `project.meta.update` to the connection that owns the checkout. */
export type GroupMetaDispatch = (update: GroupMetaUpdate) => Promise<void>;

export interface GroupMetaUpdateResult {
  readonly applied: number;
  readonly rolledBack: number;
}

/**
 * Apply a planned settings change to every checkout, or to none of them.
 *
 * All-or-nothing because a partial fan-out is the failure that matters: two
 * machines showing one name and a third showing another is both hard to notice
 * and hard to undo by hand, since nothing on screen says which one is stale.
 *
 * Sequential rather than concurrent so the set to roll back is exactly the set
 * already written. A concurrent fan-out would have to guess which of its
 * in-flight writes landed before the failure.
 */
export async function applyGroupMetaUpdate(
  plan: GroupMetaUpdatePlan,
  dispatch: GroupMetaDispatch,
): Promise<GroupMetaUpdateResult> {
  const written: GroupMetaUpdate[] = [];

  for (const update of plan.updates) {
    try {
      await dispatch(update);
      written.push(update);
    } catch {
      // Roll back only what actually landed. Restoring the checkout whose write
      // failed would push a value onto a machine this change never reached.
      return { applied: 0, rolledBack: await rollback(plan, written, dispatch) };
    }
  }

  return { applied: written.length, rolledBack: 0 };
}

/**
 * Undo the writes that landed, reporting how many were restored.
 *
 * A rollback that itself fails is counted, not thrown: the caller needs to hear
 * about the rename it asked for, and burying that under a cleanup error would
 * report the wrong problem.
 */
async function rollback(
  plan: GroupMetaUpdatePlan,
  written: readonly GroupMetaUpdate[],
  dispatch: GroupMetaDispatch,
): Promise<number> {
  let restored = 0;
  for (const update of written) {
    const prior = plan.rollback.find(
      (candidate) =>
        candidate.environmentId === update.environmentId &&
        candidate.projectId === update.projectId,
    );
    if (prior === undefined) continue;
    try {
      await dispatch(prior);
      restored += 1;
    } catch {
      // Nothing further to try for this checkout; keep restoring the others.
    }
  }
  return restored;
}
